import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';

/**
 * Generate a readable page title from the path
 */
const getPageTitle = (pathname) => {
  if (pathname === '/' || pathname === '') {
    return 'Home | Benjamin Cleek';
  }
  
  const pathParts = pathname.split('/').filter(Boolean);
  const titleParts = pathParts.map(part => {
    // Convert kebab-case and camelCase to Title Case
    return part
      .replace(/-/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  });
  
  return titleParts.join(' - ') + ' | Benjamin Cleek';
};

/**
 * Custom hook to automatically track page views on route changes
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Wait for gtag to be available and page to be ready
    const track = () => {
      const path = location.pathname + location.search;
      const title = getPageTitle(location.pathname);
      
      trackPageView(path, title);
    };

    // Check if gtag is available, if not wait a bit
    if (typeof window !== 'undefined' && window.gtag) {
      // Small delay to ensure React has updated the DOM
      const timer = setTimeout(track, 50);
      return () => clearTimeout(timer);
    } else {
      // Wait for gtag to load
      const checkGtag = setInterval(() => {
        if (typeof window !== 'undefined' && window.gtag) {
          clearInterval(checkGtag);
          track();
        }
      }, 100);
      
      // Timeout after 2 seconds
      setTimeout(() => clearInterval(checkGtag), 2000);
      
      return () => clearInterval(checkGtag);
    }
  }, [location.pathname, location.search]);
};

/**
 * Custom hook for tracking button clicks
 * Returns a function that can be used as onClick handler
 */
export const useButtonTracking = (componentName = '') => {
  const trackClick = (buttonName, additionalParams = {}) => {
    const { trackButtonClick } = require('../utils/analytics');
    trackButtonClick(buttonName, componentName, additionalParams);
  };

  return trackClick;
};
